import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "flex gap-4 justify-center items-center" }
const _hoisted_2 = { class: "w-18 text-center" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "grid grid-cols-5 px-4" }, [
  /*#__PURE__*/_createElementVNode("span", null, "Transaction item"),
  /*#__PURE__*/_createElementVNode("span", { class: "text-center" }, "Transaction type"),
  /*#__PURE__*/_createElementVNode("span", { class: "text-center" }, "Amount"),
  /*#__PURE__*/_createElementVNode("span", { class: "text-center" }, "Balance"),
  /*#__PURE__*/_createElementVNode("span", null, "Date")
], -1)
const _hoisted_4 = {
  class: "relative h-full flex flex-col gap-4 p-4 min-h-350 overflow-auto scrollbar",
  "infinite-scroll-distance": 40
}
const _hoisted_5 = { class: "capitalize" }
const _hoisted_6 = { class: "text-center" }
const _hoisted_7 = { class: "text-center" }
const _hoisted_8 = {
  key: 2,
  class: "font-medium text-center w-full text-lg my-auto"
}
const _hoisted_9 = {
  key: 3,
  class: "pl-3 text-sm fixed justify-center mt-2 flex bottom-0 w-full left-0 right-0 bg-blue-100 py-2"
}

import {
	UiCard,
	UiButton,
	SvgIcon,
	UiLoader,
	useLoading,
	Tooltip,
	useConfirmation
} from '@/ui-kit';
import { format } from 'date-fns';
import { Nullable, httpService, toCurrency } from '@/utils';
import { useToggle } from '@vueuse/core';
import { computed, ref } from 'vue';
import { CancelTokenSource } from 'axios';
import { ElInfiniteScroll as vInfiniteScroll } from 'element-plus/lib';

type HistoryItem = {
	source_type: string;
	type: 'debit' | 'credit';
	amount: string;
	balance: string;
	created_at: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'TransactionsHistory',
  setup(__props) {

const [isLoading, setIsLoading] = useToggle(true);
const [isMoreLoading, setIsMoreLoading] = useToggle(false);
const year = ref(new Date().getFullYear().toString());
const month = ref((new Date().getMonth() + 1).toString());
const items = ref<HistoryItem[]>([]);
const nextLink = ref<Nullable<string>>(null);
const cancelToken = ref<Nullable<CancelTokenSource>>(null);

fetchData();

async function loadMore() {
	if (
		isLoading.value ||
		isMoreLoading.value ||
		!nextLink.value ||
		!items.value.length
	) {
		return;
	}

	cancelToken.value = httpService.getCancelToken();
	setIsMoreLoading(true);
	const result = await httpService.get<{
		data: HistoryItem[];
		links: { next: Nullable<string> };
	}>(nextLink.value, {
		params: {
			year: year.value,
			month: month.value
		},
		...(cancelToken.value
			? { cancelToken: cancelToken.value.token }
			: {})
	});

	cancelToken.value = null;
	items.value = [
		...items.value,
		...result.data.slice().reverse()
	];
	nextLink.value = result.links.next;
	setIsMoreLoading(false);
}

const currentMonth = computed(() => {
	return format(
		new Date(`${year.value}-${month.value}`),
		'MMMM'
	);
});

async function fetchData() {
	cancelToken.value = httpService.getCancelToken();
	setIsLoading(true);
	const result = await httpService.get<{
		data: HistoryItem[];
		links: { next: Nullable<string> };
	}>('company/billing/balance-history', {
		params: {
			year: year.value,
			month: month.value
		},
		...(cancelToken.value
			? { cancelToken: cancelToken.value.token }
			: {})
	});

	cancelToken.value = null;
	items.value = result.data.slice().reverse();
	nextLink.value = result.links.next;
	setIsLoading(false);
}

async function goPrev() {
	if (cancelToken.value) {
		cancelToken.value.cancel();
	}
	setIsMoreLoading(false);

	if (month.value === '01') {
		year.value = (parseInt(year.value) - 1).toString();
		month.value = '12';
	} else {
		month.value = (parseInt(month.value) - 1)
			.toString()
			.padStart(2, '0');
	}

	fetchData();
}

async function goNext() {
	if (cancelToken.value) {
		cancelToken.value.cancel();
	}
	setIsMoreLoading(false);

	if (month.value === '12') {
		year.value = (parseInt(year.value) + 1).toString();
		month.value = '01';
	} else {
		month.value = (parseInt(month.value) + 1)
			.toString()
			.padStart(2, '0');
	}

	fetchData();
}

function getFormattedDate(date: string) {
	try {
		return format(new Date(date), 'd MMM yyyy, h:mm a');
	} catch (e) {
		console.error('Failed formatting date ', e);
		return '';
	}
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(UiCard), {
    title: "Transaction History",
    class: "relative pt-4 px-4 min-h-300",
    contentClass: "overflow-hidden flex flex-col gap-4",
    theme: "white"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(UiButton), {
          theme: "transparent",
          class: "!min-w-8 !px-0 !h-8",
          onClick: goPrev
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(SvgIcon), {
              class: "h-5 w-5 transform rotate-180",
              name: "arrow"
            })
          ]),
          _: 1
        }),
        _createElementVNode("div", null, _toDisplayString(year.value), 1),
        _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(currentMonth)), 1),
        _createVNode(_unref(UiButton), {
          theme: "transparent",
          class: "!min-w-8 !px-0 !h-8",
          onClick: goNext
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(SvgIcon), {
              class: "h-5 w-5",
              name: "arrow"
            })
          ]),
          _: 1
        })
      ]),
      _hoisted_3,
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_4, [
        (_unref(isLoading))
          ? (_openBlock(), _createBlock(_unref(UiLoader), {
              key: 0,
              loadingText: "Loading transactions...",
              wrapperClass: "base-darken text-reverse"
            }))
          : (items.value.length)
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(items.value, (item, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "grid grid-cols-5",
                  key: i
                }, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(item.source_type.replace('-', ' ')), 1),
                  _createElementVNode("span", {
                    class: _normalizeClass(["capitalize text-center", 
							item.type === 'debit'
								? 'text-red-400'
								: 'text-green-500'
						])
                  }, _toDisplayString(item.type), 3),
                  _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(toCurrency)(item.amount)), 1),
                  _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(toCurrency)(item.balance)), 1),
                  _createElementVNode("span", null, _toDisplayString(getFormattedDate(item.created_at)), 1)
                ]))
              }), 128))
            : (_openBlock(), _createElementBlock("h1", _hoisted_8, " No transactions for this period ")),
        (_unref(isMoreLoading))
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createTextVNode(" Loading more messages "),
              _createVNode(_unref(UiLoader), { class: "ml-2" })
            ]))
          : _createCommentVNode("", true)
      ])), [
        [_unref(vInfiniteScroll), loadMore]
      ])
    ]),
    _: 1
  }))
}
}

})